(ns hai.entity.link-combobox
  "Searchable entity picker for the ＋ link input — contacts, notes, and tasks.
   Mirrors `hai.tags.combobox/tag-search` — a Radix `Popover` results
   list, debounced search-as-you-type, arrow-key nav, Enter-to-pick — but has no
   \"create\" affordance since nothing is created inline, and the text field
   doubles as the URL-paste input: `on-enter-no-match` lets the host
   (`hai.entity.links/link-adder`) fall through to its existing add-URL behavior
   when Enter is pressed with no row highlighted.

   Two searches run per keystroke, because the two entity families are reachable
   through different resolvers:

   - notes and tasks via `:user/search`, the same union collection the omnibar
     runs — notes and tasks have share groups, so its `user-can-list` gate finds
     them. Its other branches (prompts, assistants, mini-apps) come back too and
     are dropped here: a union query has to name every branch, and any entity
     type that becomes linkable later shows up for free.
   - contacts via `:contact/search`, which lists the requester's own book by
     `:entity/owner`, like the contacts table, and matches
     `:contact/full-name` / `:contact/email`.

   `on-pick` receives the raw entity map; the host tells the families apart by
   the id attr it carries (`:contact/uuid` / `:note/uuid` / `:task/uuid`)."
  (:require
   [clojure.string :as str]
   [hai.contacts.helpers :as contacts]
   [hai.ui.components :as c]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [>e]]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(def ^:private debounce-ms 300)

(def ^:private label-max-chars
  "Note bodies are free text — cut the row label so one long note can't push the
   dropdown wide."
  60)

(defn- ->one-line
  [text]
  (let [flat (-> (or text "") (str/replace #"\s+" " ") str/trim)]
    (if (> (count flat) label-max-chars)
      (str (subs flat 0 label-max-chars) "…")
      flat)))

(defn ->uuid
  "The entity's id, whichever family it belongs to."
  [entity]
  (or (:contact/uuid entity) (:note/uuid entity) (:task/uuid entity)))

(defn- ->entry
  "Row data for one search hit, or nil for an entity family this picker doesn't
   link (the `:user/search` union also returns prompts, assistants, mini-apps)."
  [entity]
  (cond
    (:contact/uuid entity)
    {:key   (:contact/uuid entity)
     :label (contacts/->label+email entity)
     ;; Organisation rows get the group glyph.
     :icon  (contacts/type-icon entity)}

    (:note/uuid entity)
    {:key   (:note/uuid entity)
     :label [:span (let [text (->one-line (:note/text entity))]
                     (if (str/blank? text) "Untitled note" text))]
     :icon  icons/humble-note}

    (:task/uuid entity)
    {:key   (:task/uuid entity)
     :label [:span (->one-line (:task/title entity))]
     :icon  icons/humble-task}))

;; Separate styled component so its CSS still applies once the Popover portals
;; the list out of the combobox's DOM subtree — same trick as `tag-results`.
(o/defstyled entity-results :div
  :flex :flex-col :overflow-y-auto
  {:min-width "240px" :max-height "200px" :padding "4px 0"}
  [:.row :cursor-pointer :flex :items-center
   {:gap "8px" :padding "8px 10px" :font-size "0.875rem"}
   [#{:&:hover :&.cursor} {:background t/--card-forest-06}]]
  [:.row-icon {:display "inline-flex" :color t/--note-sage-80}
   [:svg {:width "15px" :height "15px" :fill "currentColor"}]]
  ([{:keys [entries index]}]
   (into [:<>]
         (map-indexed
          (fn [i {:keys [key label icon pick]}]
            ^{:key key}
            [:div {:class (str "row" (when (= i index) " cursor"))
                   :on-mouse-down (fn [^js e] (.preventDefault e) (pick))}
             [:span.row-icon [icon]]
             [:span label]])
          entries))))

;; Search-only combobox (no "create" row — nothing is created inline).
;; Renders its own styled `<input>` plus a portalled results dropdown. Emits a
;; pick via `on-pick`, which receives the entity map. `exclude-uuids` hides
;; already-linked entities of any family (and the host note itself).
;; `on-change` mirrors every keystroke back to the host (so `link-adder` can
;; keep validating/committing the same text as a URL). `on-enter-no-match` fires
;; on Enter when the dropdown has no highlighted row — the host's existing
;; URL-add path takes over from there.
(o/defstyled entity-search :div
  :relative :w-full
  [:.input
   {:width "100%" :box-sizing "border-box"
    :padding "4px 8px"
    :border-width "1px" :border-style "solid" :border-color t/--note-sage-35
    :border-radius "6px" :outline "none"
    :background "rgba(255,255,255,0.6)"
    :font-family "inherit" :font-size "12px"
    :color t/--charcoal}
   [:&:focus {:border-color t/--note-sage-80}]
   [:&.invalid {:border-color t/--property-13}]
   [:&::placeholder {:color t/--note-sage-55}]]
  [:.anchor :w-0 :h-0]
  ([{:keys [on-pick on-enter-no-match on-change on-cancel value invalid?
            placeholder exclude-uuids auto-focus?]}]
   (r/with-let [!contacts    (r/atom [])
                !entities    (r/atom [])
                !open?       (r/atom false)
                !index       (r/atom 0)
                !timer       (atom nil)
                ;; Guards against a late-resolving debounced query re-opening the
                ;; dropdown (with stale results) after the user has kept typing —
                ;; e.g. cleared a name search and pasted a URL before the earlier
                ;; request came back. Only the most-recently-scheduled query may
                ;; touch the result atoms / `!open?`. Both searches check it, so
                ;; whichever lands first opens the list and the other fills in
                ;; behind it.
                !latest-text (atom nil)
                receive!  (fn [!target text results]
                            (when (= text @!latest-text)
                              (reset! !target results)
                              (reset! !index 0)
                              (reset! !open? true)))
                search!   (fn [text]
                            (if (str/blank? text)
                              (do (reset! !contacts [])
                                  (reset! !entities [])
                                  (reset! !open? false))
                              (do
                                (>e [:resolve
                                     [{(list :contact/search {:contact/search-text text})
                                       [:contact/uuid :contact/full-name :contact/email
                                        :contact/organisation?]}]
                                     {:on-success
                                      (fn [resp]
                                        (receive! !contacts text
                                                  (get resp :contact/search [])))}])
                                (>e [:resolve
                                     [{(list :user/search {:search text})
                                       [{:collection/items
                                         {:note [:note/uuid :note/text]
                                          :task [:task/uuid :task/title]
                                          :prompt [:ai-prompt/title]
                                          :assistant [:ai-model-config/uuid]
                                          :mini-app [:mini-app/uuid]
                                          :contact [:contact/uuid]}}]}]
                                     {:on-success
                                      (fn [resp]
                                        (receive! !entities text
                                                  ;; Keep only what a note can
                                                  ;; link. Contacts come from
                                                  ;; `:contact/search` — this
                                                  ;; resolver can't see them.
                                                  (->> (get-in resp [:user/search :collection/items])
                                                       (filterv #(or (:note/uuid %)
                                                                     (:task/uuid %))))))}]))))
                schedule! (fn [text]
                            (reset! !latest-text text)
                            (when-let [t @!timer] (js/clearTimeout t))
                            (reset! !timer (js/setTimeout #(search! text) debounce-ms)))]
     (let [text     (or value "")
           ex-uuids (set exclude-uuids)
           results  (remove #(contains? ex-uuids (->uuid %))
                            (concat @!contacts @!entities))
           pick!    (fn [entity]
                      (on-pick entity)
                      (reset! !contacts [])
                      (reset! !entities [])
                      (reset! !open? false)
                      (reset! !index 0))
           entries  (into []
                          (keep (fn [entity]
                                  (some-> (->entry entity)
                                          (assoc :pick #(pick! entity)))))
                          results)
           index    (max 0 (min @!index (dec (count entries))))
           open?    (and @!open? (seq entries))]
       [:<>
        [c/PopoverRoot
         ;; Controlled, so Radix's own dismiss (outside pointer-down, Escape)
         ;; can't close it on its own — mirror the close back into `!open?`.
         {:open (boolean open?)
          :onOpenChange (fn [next-open?] (when-not next-open? (reset! !open? false)))}
         [:input.input
          {:value       text
           :class       (when invalid? "invalid")
           :placeholder (or placeholder "Search or paste a link…")
           :auto-focus  (boolean auto-focus?)
           :aria-label  "Search contacts, notes and tasks, or paste a link"
           :aria-invalid (boolean invalid?)
           ;; Rows pick on mouse-down with `preventDefault`, so blur can't beat
           ;; a click on a result. Clicking off works as Enter: commit typed
           ;; text through the URL-add fallback, collapse the section when
           ;; blank.
           :on-blur     (fn [_]
                          (reset! !open? false)
                          (if (str/blank? text)
                            (when on-cancel (on-cancel))
                            (when on-enter-no-match (on-enter-no-match))))
           :on-change   (fn [^js e]
                          (let [v (.. e -target -value)]
                            (on-change v)
                            (reset! !index 0)
                            (schedule! v)))
           :on-key-down (fn [^js e]
                          (case (.-key e)
                            "ArrowDown" (when (seq entries)
                                          (.preventDefault e)
                                          (reset! !index (min (inc index) (dec (count entries)))))
                            "ArrowUp"   (when (seq entries)
                                          (.preventDefault e)
                                          (reset! !index (max 0 (dec index))))
                            ;; First Escape closes an open dropdown; with it
                            ;; already closed, Escape collapses the section
                            ;; (like `tag-search`/`link-adder`'s plain input).
                            "Escape"    (if @!open?
                                          (do (.stopPropagation e) (reset! !open? false))
                                          (when on-cancel
                                            (.stopPropagation e)
                                            (on-cancel)))
                            "Enter"     (do (.preventDefault e)
                                            (if (and open? (seq entries))
                                              ((:pick (nth entries index)))
                                              ;; No match (or dropdown not open)
                                              ;; — hand off to the URL-add
                                              ;; fallback.
                                              (when on-enter-no-match (on-enter-no-match))))
                            nil))}]
         ;; The Trigger is a decoy zero-size anchor, not the input itself — a
         ;; live, re-rendering controlled `<input>` (new element/props every
         ;; keystroke) as a Radix asChild trigger causes Popper to lose its
         ;; anchor ref right when the popover needs to open. Mirrors
         ;; `hai.tags.combobox/tag-search`'s `.anchor` pattern.
         [c/PopoverTrigger [:div.anchor]]
         [c/PopoverContent
          {:onOpenAutoFocus (fn [^js e] (.preventDefault e))
           :side "bottom"
           :align "start"
           :sideOffset 4}
          [c/Inset
           [entity-results {:entries entries :index index}]]]]])
     (finally
       (when-let [t @!timer] (js/clearTimeout t))))))
